import { normalizeEnumOptions, normalizeEnumOptionsSortMode } from 'utilities/EnumOptions';
import { normalizeMeasurementDefaultUnit, normalizeMeasurementOptions } from 'utilities/Measurement';

export const validateDraft = (draft, options = {}) => {
    const unavailableTypes = Array.isArray(options.unavailableTypes)
        ? options.unavailableTypes
        : [];
    const trimmedLabel = draft.label.trim();
    const nextErrors = { type: '', label: '', options: '', snapTo: '', targetType: '', defaultUnit: '' };
    const isValidLabel = /^[A-Za-z0-9()\- ]+$/.test(trimmedLabel);

    if (unavailableTypes.includes(draft.type)) {
        nextErrors.type = 'This field type can only be added once.';
    }

    if (!trimmedLabel) {
        nextErrors.label = 'Title is required.';
    } else if (!isValidLabel) {
        nextErrors.label = 'Use letters, numbers, spaces, parentheses, and dashes only.';
    }

    if (draft.type === 'nodelist' && !String(draft.targetType || '').trim()) {
        nextErrors.targetType = 'Select a node type.';
    }

    if (draft.type === 'enum' || draft.type === 'multiselect') {
        const rawOptions = Array.isArray(draft.options) ? draft.options : [];
        const normalizedOptions = normalizeEnumOptions(rawOptions);
        const rawTrimmed = rawOptions
            .map((opt) => {
                if (typeof opt === 'string') return opt.trim();
                if (opt && typeof opt === 'object') {
                    const candidate = opt.value ?? opt.label ?? '';
                    return typeof candidate === 'string' ? candidate.trim() : String(candidate);
                }
                return '';
            })
            .filter(Boolean);
        const optionValues = normalizedOptions
            .map((opt) => opt?.value)
            .filter((value) => typeof value === 'string' && value.trim().length);

        if (optionValues.length === 0) {
            nextErrors.options = 'Add at least one option.';
        } else if (rawTrimmed.length !== optionValues.length) {
            nextErrors.options = 'Options must be unique.';
        } else if (draft.type === 'enum' && draft.default && !optionValues.includes(draft.default)) {
            nextErrors.options = 'Default must match one of the options.';
        } else if (draft.type === 'enum') {
            const rawSortMode = String(draft.enumOptionsSort || '').trim().toLowerCase();
            if (rawSortMode && normalizeEnumOptionsSortMode(rawSortMode) !== rawSortMode) {
                nextErrors.options = 'Invalid enum sort mode.';
            }
        }
    }

    if (draft.type === 'measurement') {
        const rawOptions = Array.isArray(draft.options) ? draft.options : [];
        const normalizedOptions = normalizeMeasurementOptions(rawOptions);
        const rawTrimmed = rawOptions
            .map((opt) => {
                if (typeof opt === 'string') return opt.trim();
                if (opt && typeof opt === 'object') {
                    const candidate = opt.value ?? opt.label ?? '';
                    return typeof candidate === 'string' ? candidate.trim() : String(candidate);
                }
                return '';
            })
            .filter(Boolean);
        const optionValues = normalizedOptions
            .map((opt) => opt?.value)
            .filter((value) => typeof value === 'string' && value.trim().length);

        if (optionValues.length === 0) {
            nextErrors.options = 'Add at least one unit.';
        } else if (rawTrimmed.length !== optionValues.length) {
            nextErrors.options = 'Units must be unique.';
        }

        if (!normalizeMeasurementDefaultUnit(draft.defaultUnit, normalizedOptions)) {
            nextErrors.defaultUnit = 'Select a default unit.';
        }
    }

    if (draft.type === 'percentage') {
        const rawDefault = draft.default;
        if (rawDefault !== '' && rawDefault !== undefined && rawDefault !== null) {
            const numeric = Number(rawDefault);
            if (!Number.isFinite(numeric)) {
                nextErrors.options = 'Default percentage must be a number between 0 and 100.';
            } else if (!Number.isInteger(numeric)) {
                nextErrors.options = 'Default percentage must be a whole number.';
            } else if (numeric < 0 || numeric > 100) {
                nextErrors.options = 'Default percentage must be between 0 and 100.';
            }
        }

        const snapRaw = draft.snapTo;
        const snapNumeric = Number(snapRaw);
        if (!Number.isFinite(snapNumeric) || !Number.isInteger(snapNumeric) || snapNumeric <= 0) {
            nextErrors.snapTo = 'Snap must be a whole number greater than 0.';
        }
    }

    return {
        errors: nextErrors,
        isValid: !nextErrors.type && !nextErrors.label && !nextErrors.options && !nextErrors.snapTo && !nextErrors.targetType && !nextErrors.defaultUnit,
    };
};
